import React, { useEffect, useMemo, useRef } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { PRIMARY_NAV, NAV_OF, routeKeyOf } from "../../resources/menu";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import { CHAT_ICON, fmtCount } from "./Header";
import { MdClose, MdLogout } from "react-icons/md";

/**
 * Navigation Rail 的一格（Material Design 3）：圖示在上、文字在下，
 * 目前所在的入口在圖示後面畫一顆膠囊形的高亮；滑過時整格（連同文字）上底色。
 * 傳 to 就是 react-router 的 <Link>（換頁），沒傳就是 <button>（做一件事）。
 * 整格至少 --hit-min 高，圖示與文字都跟著 --icon / --t-meta 放大。
 */
function RailItem({ to, icon, label, active, badge, onClick, ariaLabel, ...rest }) {
  const linkProps = to ? { component: Link, to, "aria-current": active ? "page" : undefined } : { component: "button", type: "button" };
  return (
    <ButtonBase
      {...linkProps}
      {...rest}
      onClick={onClick}
      aria-label={ariaLabel}
      sx={{
        width: "100%",
        minHeight: "var(--hit-min)",
        py: 0.75,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        borderRadius: "var(--radius-sm)",
        color: active ? "text.primary" : "text.secondary",
        /* 滑過時整格（圖示＋下方文字）一起上底色，看得出可以按的範圍就是這一整格 */
        transition: "background-color .15s",
        "&:hover": { bgcolor: "action.hover" },
        "&.Mui-focusVisible": { outline: "var(--focus-w) solid var(--focus-ring)", outlineOffset: 2 },
      }}
    >
      <Box
        className="rail-pill"
        sx={{
          width: 56,
          height: 32,
          borderRadius: "var(--radius-pill)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "var(--icon)",
          bgcolor: active ? "primary.light" : "transparent",
          color: active ? "primary.main" : "inherit",
          transition: "background-color .15s",
        }}
        aria-hidden="true"
      >
        <Badge badgeContent={badge || null} color="error" max={99}>
          <Box component="span" sx={{ display: "flex" }}>
            {icon}
          </Box>
        </Badge>
      </Box>
      {label ? (
        <Box
          component="span"
          sx={{ fontSize: "var(--t-meta)", lineHeight: 1.2, fontWeight: active ? 700 : 600, whiteSpace: "nowrap" }}
        >
          {label}
        </Box>
      ) : null}
    </ButtonBase>
  );
}

RailItem.propTypes = {
  to: PropTypes.string,
  icon: PropTypes.node,
  label: PropTypes.string,
  active: PropTypes.bool,
  badge: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClick: PropTypes.func,
  ariaLabel: PropTypes.string,
};

RailItem.defaultProps = { to: "", icon: null, label: "", active: false, badge: 0, onClick: undefined, ariaLabel: undefined };

/**
 * 側邊欄 —— MUI 組成的 Navigation Rail（Material Design 3）。
 *
 * 舊版是 22 列、5 個分組、可拖曳寬度、拖窄後只剩圖示的軌道。三件事一起拿掉：
 *   · 分組與 22 列 → PRIMARY_NAV 那幾個入口，其餘畫面從入口底下進去；
 *   · 拖曳寬度 → 寬度由 --sidebar-w / --sidebar-rail 決定，少一份要記在
 *     localStorage 的裝置狀態，也少一個沒有文字的可拖曳把手；
 *   · 軌道模式藏文字 → 違反設計規則第 5 條「收合後仍保留圖示＋文字」；
 *     現在是 Material Design 3 的 Navigation Rail，一律圖示在上、文字在下。
 *
 * 品牌區、全域搜尋鈕、使用者卡、改密碼與登出都不在新側欄的語彙裡：
 * 搜尋由頁首的「搜尋」鈕承擔，使用者卡與改密碼搬到「我的帳號」頁。
 * 登出暫時留在這裡（見下方註解），否則使用者會登不出去。
 *
 * 手機抽屜（!wide）另外多一顆「聊天」。手機頁首擠不下，從頁首搬進來
 * （見 Header.js 檔頭）；桌機頁首照舊有，所以桌機側欄不畫。
 * 深淺色與字級在頁首的帳號下拉裡，手機也看得到，抽屜不再另放一份。
 * 最上面還有一顆「收起選單」—— 抽屜會蓋住頁首的選單鈕，理由見下面那一段。
 */
function Sider(props) {
  const {
    wide,
    onNavigate,
    approvalsCount,
    onLogout,
    permissions,
    chatUnread,
    onOpenChat,
    onClose,
  } = props;
  const location = useLocation();
  const navRef = useRef(null);

  /*
   * 抽屜一打開就把焦點移進來。
   *
   * 抽屜在 DOM 裡排在 .el-main 前面，焦點留在頁首「選單」鈕的話，往下 Tab 會跑進
   * 被遮罩蓋住的內容區，要繞一整圈才回得到抽屜。桌機的側欄是常駐的，不搶焦點。
   * 用滑鼠或手指點開的人照理不會看到焦點框：:focus-visible 的規範是程式移過去的
   * 焦點，只有上一個動作是鍵盤時才畫框。（沒有在 iPhone 實機上驗過這一點。）
   */
  useEffect(() => {
    if (wide || !navRef.current) return;
    const first = navRef.current.querySelector("a[href]");
    if (first) first.focus();
  }, [wide]);

  /**
   * 依權限過濾側欄入口。
   *
   * 沒有權限的入口一律不顯示 —— 看得到卻按了 403，使用者會以為系統壞掉，
   * 而不是自己沒被授權。PRIMARY_NAV 上的 permission 值就是後端對應 Controller
   * 的 [PermissionOnly]，兩邊必須一致。permission 為 null 代表登入即可用。
   *
   * anyPermission 優先於 permission：「檢測」底下的原碼檢測與弱點掃描是兩個
   * 分開授權的模組，只有其中一個權限的人照樣該看得到這個入口。
   *
   * 權限還沒載回來時只顯示不需授權的那幾項（今天、設定）。原本是「先全部顯示
   * 避免閃爍」，但那等於每次進站都閃一次沒權限的入口，正是這裡要解決的問題。
   *
   * 兩支 API 回的形狀不同：/Auth/RolePermissions 是扁平字串陣列（Role.PermissionsUser），
   * /Auth/Function 是 [{id,name,module,...}]（Auth.Function）。這裡一併吸收，
   * 否則拿物件去比字串會全部落空、側欄整個空白。
   */
  const items = useMemo(() => {
    const perms = (permissions || []).map((p) =>
      typeof p === "string" ? p : _.get(p, "module", "")
    );
    return PRIMARY_NAV.filter((item) => {
      if (item.anyPermission) return item.anyPermission.some((code) => perms.includes(code));
      if (!item.permission) return true;
      return perms.includes(item.permission);
    });
  }, [permissions]);

  /*
   * 目前位置 → 側欄入口其中之一。
   *
   * 先用 routeKeyOf 把網址換成畫面 key，再查 NAV_OF。不直接比 path 前綴是因為
   * 好幾個子畫面根本不在入口的前綴底下：/Approvals 屬於「今天」、/Board 屬於
   * 「專案」、/Task/12 屬於「工項」。查不到就是所有入口都不亮 —— 那是誠實的
   * 結果（例如 /Guide 之外的孤兒頁），不要硬塞一個入口讓它亮。
   */
  const currentNav = NAV_OF[routeKeyOf(location.pathname)];

  /*
   * 定位：
   *   桌機：sticky + 100vh —— 內容很長時捲下去側欄還在。
   *   手機：fixed 抽屜，z-index:80 剛好疊在 Layout 那層 z-index:79 的遮罩上面。
   *         高度用 top:0 + bottom:0 撐，不用 100vh：手機瀏覽器的 100vh 是工具列收起來時的高度，
   *         工具列展開時抽屜最底那一截（登出）會落在工具列底下按不到。
   * flex column 讓底部的登出能用 margin-top:auto 沉到最下面。
   */
  const frame = wide
    ? { position: "sticky", top: 0, height: "100vh", flexShrink: 0 }
    : { position: "fixed", top: 0, bottom: 0, left: 0, zIndex: 80, boxShadow: 8 };

  return (
    <Box
      component="nav"
      ref={navRef}
      aria-label="主導覽"
      sx={{
        ...frame,
        width: 88,
        px: 1,
        py: 1.5,
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        bgcolor: "background.paper",
        borderRight: 1,
        borderColor: "divider",
      }}
    >
      {/*
        手機抽屜最上面一顆「收起選單」：抽屜貼著畫面左上角打開、z-index 比頁首高，
        正好蓋住頁首的「選單」鈕。同一個位置再按一次，應該是收起來，不是點到第一個入口換頁。
      */}
      {!wide ? <RailItem icon={<MdClose />} label="收起選單" onClick={onClose} /> : null}

      {items.map((item) => (
        <RailItem
          key={item.key}
          to={item.path}
          data-nav={item.key}
          icon={item.icon}
          label={item.label}
          active={currentNav === item.key}
          badge={item.badge === "approvals" ? approvalsCount : 0}
          onClick={() => {
            if (onNavigate) onNavigate();
          }}
        />
      ))}

      {/*
        手機抽屜才有的「聊天」：從頁首搬進來，是動作不是換頁，所以是 <button>。
        用細線跟上面的入口隔開：上面是「去哪一頁」，這裡是「做一件事」。
        聊天按下去先收抽屜再開面板（面板關掉後抽屜不要還攤著擋內容）。
      */}
      {!wide ? (
        <>
          <Divider sx={{ my: 1 }} />
          <RailItem icon={CHAT_ICON} label="聊天" badge={chatUnread > 0 ? fmtCount(chatUnread) : 0} onClick={onOpenChat} />
        </>
      ) : null}

      {/*
        登出暫放在側欄最底 —— 設計稿把它歸到「我的帳號」頁（頁首的帳號選單也有一份）。
        跟上面的入口一樣圖示在上、文字在下，文字已經講清楚了，不再另掛 tooltip。
      */}
      <Box sx={{ mt: "auto" }}>
        <RailItem icon={<MdLogout />} label="登出" onClick={onLogout} />
      </Box>
    </Box>
  );
}

/*
 * Global.userInfo 不再取 —— 使用者卡（頭像、姓名、線上狀態）整塊移到「我的帳號」頁，
 * 側欄已經沒有地方顯示它。留著等於讓 Sider 每次 userInfo 變動都多重繪一次。
 */
const mapStateToProps = (state) => ({
  // 優先用扁平字串陣列（/Auth/RolePermissions），沒有才退回物件陣列（/Auth/Function）
  permissions: _.get(state, "Role.PermissionsUser", undefined) || _.get(state, "Auth.Function", undefined),
});

Sider.propTypes = {
  wide: PropTypes.bool,
  collapsed: PropTypes.bool,
  onNavigate: PropTypes.func,
  approvalsCount: PropTypes.number,
  onLogout: PropTypes.func,
  permissions: PropTypes.array,
  chatUnread: PropTypes.number,
  onOpenChat: PropTypes.func,
  onClose: PropTypes.func,
};

Sider.defaultProps = {
  wide: true,
  collapsed: false,
  onNavigate: null,
  approvalsCount: 0,
  onLogout: () => {},
  permissions: undefined,
  chatUnread: 0,
  onOpenChat: () => {},
  onClose: () => {},
};

export default connect(mapStateToProps)(Sider);
